// SnapComposer — Snapchat-style camera-first snap composer.
// Grabs the user's front camera, shows a full-bleed preview with a swipeable filter carousel,
// tap-to-snap capture button, retake, and Send-to-DM. Ships with 12 CSS filter presets
// (no MediaPipe face tracking — that lands as an AR Mode upgrade next round).
//
// Usage:
//   <SnapComposer onClose={fn} onSend={(dataUrl, filter) => {...}} />
//
// The parent handles delivery — SnapComposer only produces a data-URL PNG snapshot.

import { useEffect, useRef, useState } from "react";
import { Camera, X, RefreshCw, Send, RotateCcw, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

const FILTERS = [
  { id: "none",       label: "Original",  css: "none" },
  { id: "grayscale",  label: "B&W",       css: "grayscale(1) contrast(1.05)" },
  { id: "sepia",      label: "Sepia",     css: "sepia(0.9) contrast(1.05)" },
  { id: "cool",       label: "Frost",     css: "hue-rotate(200deg) saturate(1.3) brightness(1.05)" },
  { id: "warm",       label: "Sunset",    css: "hue-rotate(-25deg) saturate(1.4) brightness(1.08)" },
  { id: "vhs",        label: "VHS",       css: "contrast(1.35) saturate(1.4) hue-rotate(-8deg) brightness(0.98)" },
  { id: "neon",       label: "Neon",      css: "saturate(2) contrast(1.2) hue-rotate(280deg)" },
  { id: "pink",       label: "Bubblegum", css: "hue-rotate(320deg) saturate(1.6) brightness(1.05)" },
  { id: "cyberpunk",  label: "Cyberpunk", css: "hue-rotate(180deg) saturate(1.8) contrast(1.25)" },
  { id: "dream",      label: "Dream",     css: "blur(0.7px) saturate(1.3) brightness(1.08) contrast(0.95)" },
  { id: "noir",       label: "Noir",      css: "grayscale(1) contrast(1.4) brightness(0.9)" },
  { id: "polaroid",   label: "Polaroid",  css: "sepia(0.4) saturate(1.2) contrast(1.1) brightness(1.05)" },
];

export default function SnapComposer({ onClose, onSend }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [filterIdx, setFilterIdx] = useState(0);
  const [facing, setFacing] = useState("user"); // "user" | "environment"
  const [snapshot, setSnapshot] = useState(null); // data-URL after capture
  const [caption, setCaption] = useState("");
  const [starting, setStarting] = useState(true);
  const [permError, setPermError] = useState(null);

  useEffect(() => { start(); return () => stop(); }, [facing]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    stop();
    setStarting(true); setPermError(null);
    let s;
    try {
      s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
    } catch (e) {
      setPermError(e?.message || "Camera unavailable");
      setStarting(false);
      return;
    }
    streamRef.current = s;
    const v = videoRef.current;
    if (v) {
      try { v.srcObject = s; } catch { /* ignore */ }
      // Attempt play(); browser will also autoPlay. Swallow AbortError from
      // React's re-mount / re-render churn — the video still shows.
      const p = v.play?.();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
    setStarting(false);
  };
  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const swipe = (dir) => setFilterIdx((i) => (i + dir + FILTERS.length) % FILTERS.length);

  const snap = () => {
    const v = videoRef.current;
    if (!v || v.readyState < 2) return;
    const cvs = canvasRef.current;
    const w = v.videoWidth, h = v.videoHeight;
    cvs.width = w; cvs.height = h;
    const ctx = cvs.getContext("2d");
    // Burn the filter into the exported PNG
    ctx.filter = FILTERS[filterIdx].css;
    ctx.save();
    if (facing === "user") { ctx.translate(w, 0); ctx.scale(-1, 1); } // mirror selfies
    ctx.drawImage(v, 0, 0, w, h);
    ctx.restore();
    // Caption overlay
    if (caption.trim()) {
      ctx.filter = "none";
      ctx.textAlign = "center";
      ctx.font = `bold ${Math.round(w * 0.045)}px system-ui, sans-serif`;
      const y = Math.round(h * 0.88);
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      const metrics = ctx.measureText(caption);
      ctx.fillRect((w - metrics.width) / 2 - 20, y - Math.round(w * 0.045), metrics.width + 40, Math.round(w * 0.06));
      ctx.fillStyle = "#fff";
      ctx.fillText(caption, w / 2, y);
    }
    setSnapshot(cvs.toDataURL("image/jpeg", 0.85));
    stop();
  };

  const retake = () => { setSnapshot(null); start(); };

  const send = () => { if (snapshot) onSend?.(snapshot, FILTERS[filterIdx].id, caption); };

  return (
    <div className="fixed inset-0 z-[80] flex flex-col select-none" data-testid="snap-composer" style={{ backgroundColor: "#000", color: "#fff" }}>
      {/* Top bar */}
      <div className="flex items-center justify-between p-3 text-white">
        <button onClick={() => { stop(); onClose?.(); }} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="snap-close"><X className="w-5 h-5" /></button>
        <div className="text-[11px] tracking-[0.3em] uppercase font-display text-white/70">// snap composer</div>
        <button onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))} disabled={!!snapshot} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30" data-testid="snap-flip-camera" title="Flip camera">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Camera view */}
      <div className="relative flex-1 overflow-hidden bg-black flex items-center justify-center">
        {!snapshot ? (
          <>
            <video
              ref={videoRef}
              className={`w-full h-full object-cover ${facing === "user" ? "scale-x-[-1]" : ""}`}
              style={{ filter: FILTERS[filterIdx].css, transition: "filter 200ms ease" }}
              autoPlay
              playsInline
              muted
              data-testid="snap-video"
            />
            {starting && <div className="absolute inset-0 flex items-center justify-center text-white/60 text-xs tracking-widest font-display">// warming up camera…</div>}
            {permError && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                <div className="max-w-sm text-white/85 space-y-2">
                  <div className="text-lg font-display font-bold">Camera blocked</div>
                  <div className="text-sm text-white/60">{permError}</div>
                  <div className="text-xs text-white/40">Allow camera in your browser settings and reload.</div>
                </div>
              </div>
            )}
            {/* Filter carousel arrows */}
            <button onClick={() => swipe(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-black/70" data-testid="snap-filter-prev"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={() => swipe(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-black/70" data-testid="snap-filter-next"><ChevronRight className="w-6 h-6" /></button>
            {/* Filter label */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur text-white px-3 py-1.5 rounded-full text-[11px] font-display tracking-[0.3em] uppercase" data-testid="snap-filter-label">{FILTERS[filterIdx].label}</div>
          </>
        ) : (
          <img src={snapshot} alt="snap" className="w-full h-full object-contain" data-testid="snap-preview" />
        )}
      </div>

      {/* Caption + controls */}
      <div className="p-3 space-y-3 bg-black">
        {!snapshot && (
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 80))}
            placeholder="Add a caption…"
            className="w-full bg-white/10 text-white placeholder-white/40 rounded-full px-4 py-2 text-sm outline-none"
            data-testid="snap-caption"
          />
        )}
        <div className="flex items-center justify-center gap-6">
          {!snapshot ? (
            <button onClick={snap} disabled={starting || !!permError} className="w-20 h-20 rounded-full bg-white ring-4 ring-white/40 hover:ring-white/70 transition disabled:opacity-30 relative" data-testid="snap-capture" aria-label="capture">
              <div className="absolute inset-2 rounded-full border-2 border-black/20"></div>
              <Camera className="w-7 h-7 text-black mx-auto" />
            </button>
          ) : (
            <>
              <button onClick={retake} className="rounded-full bg-white/10 hover:bg-white/20 text-white px-5 py-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-display" data-testid="snap-retake"><RotateCcw className="w-4 h-4" /> Retake</button>
              <button onClick={send} className="rounded-full bg-gradient-to-r from-nexus-pink to-nexus-magenta text-white px-6 py-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-display shadow-lg" data-testid="snap-send"><Send className="w-4 h-4" /> Send</button>
            </>
          )}
        </div>
        {!snapshot && (
          <div className="flex items-center justify-center gap-1 pt-1 flex-wrap" data-testid="snap-filter-chips">
            {FILTERS.map((f, i) => (
              <button
                key={f.id}
                onClick={() => setFilterIdx(i)}
                className={`text-[10px] font-display tracking-[0.15em] uppercase px-2.5 py-1 rounded-full border transition ${filterIdx === i ? "bg-white text-black border-white" : "text-white/70 border-white/20 hover:border-white/50"}`}
                data-testid={`snap-filter-${f.id}`}
              >{f.label}</button>
            ))}
            <div className="text-[10px] text-white/40 pl-2 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Swipe with ← →</div>
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
